import React, { useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';

import { CommonComponentProps, ImageObject } from '../pages/types';
import ImageFullscreen from './image-fullscreen';

interface ImageCarouselProps extends CommonComponentProps {
  images: ImageObject[];
  carouselHeight: string | number;
}

const Carousel: React.FC<ImageCarouselProps> = ({
  images,
  carouselHeight: height,
  isEditable,
  onImageUpload,
  onPropsChange,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleImageClick = (index: number) => {
    if (isEditable && onImageUpload && onPropsChange) {
      onImageUpload((result) => {
        const url = Array.isArray(result) ? result[0] : result;
        const newImages = [...images];
        newImages[index] = {
          ...newImages[index],
          url: url,
        };
        onPropsChange({ images: newImages });
      });
    }
  };

  const handleAddImage = () => {
    if (isEditable && onImageUpload && onPropsChange) {
      onImageUpload(
        (result) => {
          const urls = Array.isArray(result) ? result : [result];
          const newImages = [
            ...images,
            ...urls.map((url) => ({ url, title: '', altText: '' })),
          ];
          onPropsChange({ images: newImages });
        },
        { multiple: true },
      );
    }
  };

  // Show at least 3 empty "add image" slots when the carousel is new/empty,
  // and always keep one trailing slot available to add more images.
  const placeholderCount = isEditable ? Math.max(3 - images.length, 1) : 0;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scrollRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - scrollRef.current.offsetLeft);
      setScrollLeft(scrollRef.current.scrollLeft);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Adjust the scroll speed
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (scrollRef.current) {
      setIsDragging(true);
      setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
      setScrollLeft(scrollRef.current.scrollLeft);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollRef.current) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Adjust the scroll speed
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <>
      <Box
        sx={{
          py: '10px',
          position: 'relative',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          height: height,
          overflow: 'hidden',
          overflowX: 'scroll',
          '&::-webkit-scrollbar': { display: 'none' },
          alignSelf: 'center',
        }}
      >
        <Box
          ref={scrollRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onDragStart={(e) => e.preventDefault()} // Prevent default drag behavior
          sx={{
            height: height,
            width: '100%',
            display: 'flex',
            overflow: 'auto',
            whiteSpace: 'nowrap',
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': { display: 'none' },
            cursor: isDragging ? 'grabbing' : 'grab', // Change cursor during dragging
          }}
        >
          {images.map((image, index) => {
            const content = (
              <Box
                component="img"
                src={image.url}
                alt={image.altText || `Image ${index + 1}`}
                sx={{
                  flex: '0 0 auto',
                  width: 'auto',
                  height: '99%',
                  marginRight: '8px',
                  objectFit: 'cover',
                  cursor: isEditable ? 'pointer' : 'pointer',
                  '&:hover': isEditable
                    ? {
                        outline: '2px dashed #1976d2',
                        zIndex: 1,
                      }
                    : {},
                }}
                onClick={(e) => {
                  if (isEditable) {
                    e.stopPropagation();
                    handleImageClick(index);
                  } else {
                    setCurrentIndex(index);
                  }
                }}
              />
            );

            return isEditable ? (
              <Box key={index} sx={{ height: '100%', display: 'inline-block' }}>
                {content}
              </Box>
            ) : (
              <ImageFullscreen
                firstImage={image}
                images={images}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                key={index}
              >
                {content}
              </ImageFullscreen>
            );
          })}
          {isEditable &&
            Array.from({ length: placeholderCount }).map((_, i) => (
              <Box
                key={`placeholder-${i}`}
                onClick={handleAddImage}
                sx={{
                  flex: '0 0 auto',
                  width: height,
                  height: '99%',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  cursor: 'pointer',
                  border: '2px dashed #ccc',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    borderColor: '#666',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 48, color: '#999' }} />
              </Box>
            ))}
        </Box>
      </Box>
    </>
  );
};

export default Carousel;
